import React, { useCallback, useEffect, useState } from 'react';
import {
  UserCog, Plus, Ban, RotateCcw, Mail, MapPin, Stethoscope, Clock, AlertTriangle,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatMoney, formatDateTime } from '../i18n';
import {
  Card, Button, Input, Select, Field, Badge, Alert, Spinner, EmptyState, Modal, Table,
} from '../components/ui';

/**
 * Practice roster, admin only.
 *
 * Admin does not create logins: creating a Supabase Auth user needs the service
 * key, which must never reach the browser. Instead admin records the
 * professional with their email address, and that person claims it by
 * registering with the same address — matched on their verified email, so a
 * record can only be claimed by whoever controls the inbox.
 */

const ProfessionalsView = () => {
  const [therapists, setTherapists] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [draft, setDraft] = useState({ email: '', firstName: '', lastName: '', disciplineId: '', license: '', phone: '' });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [therapistResult, disciplineResult, locationResult, blockResult] = await Promise.all([
      supabase
        .from('therapists')
        .select('id, email, first_name, last_name, phone, license_number, is_admin, is_active, is_blocked, auth_user_id, primary_discipline_id, default_private_fee, default_session_minutes, approval_status, approval_requested_at, fee_accepted_at, fee_accepted_monthly, disciplines:primary_discipline_id ( name )')
        .order('last_name'),
      supabase.from('disciplines').select('id,name').eq('is_active', true).order('name'),
      supabase.from('locations').select('id,name').eq('is_active', true).order('name'),
      supabase.from('therapist_availability').select('therapist_id, location_id, allows_online'),
    ]);

    if (therapistResult.error) setError(therapistResult.error.message);
    setTherapists(therapistResult.data ?? []);
    setDisciplines(disciplineResult.data ?? []);
    setLocations(locationResult.data ?? []);

    // Where each professional works is where they put their hours (0044):
    // their sedes, and online if any of their hours take online sessions.
    const grouped = {};
    for (const block of blockResult.data ?? []) {
      const entry = (grouped[block.therapist_id] ??= { sedeIds: new Set(), online: false });
      if (block.location_id) entry.sedeIds.add(block.location_id);
      if (block.allows_online) entry.online = true;
    }
    setAssignments(grouped);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  /** A professional's places, by name: their sedes, then online. */
  const placesOf = (entry) => (entry ? [
    ...[...entry.sedeIds].map((id) => locations.find((location) => location.id === id)?.name).filter(Boolean),
    ...(entry.online ? [t('availability.online')] : []),
  ] : []);

  // Self-applied professionals awaiting a decision. Nothing they do is live
  // until approved, so they are kept separate from the working roster.
  const pending = therapists.filter((row) => row.approval_status === 'pending');
  const roster = therapists.filter((row) => row.approval_status !== 'pending');

  const review = async (therapist, approve) => {
    if (!window.confirm(t(approve ? 'professionals.confirmApprove' : 'professionals.confirmReject',
      { name: therapist.first_name + ' ' + therapist.last_name }))) return;
    setBusy(true);
    const { error: reviewError } = await supabase.rpc('review_professional_request', {
      p_therapist_id: therapist.id,
      p_approve: approve,
      p_discipline_id: therapist.primary_discipline_id,
      p_reason: null,
    });
    setBusy(false);
    if (reviewError) { setError(reviewError.message); return; }
    setNotice(t(approve ? 'professionals.approved' : 'professionals.rejected',
      { name: therapist.first_name + ' ' + therapist.last_name }));
    load();
  };

  const invite = async () => {
    setBusy(true);
    setError(null);
    const { error: inviteError } = await supabase.rpc('invite_therapist', {
      p_email: draft.email.trim(),
      p_first_name: draft.firstName.trim(),
      p_last_name: draft.lastName.trim(),
      p_primary_discipline_id: draft.disciplineId || null,
      p_license_number: draft.license || null,
      p_phone: draft.phone || null,
    });
    setBusy(false);
    if (inviteError) { setError(inviteError.message); return; }
    setInviting(false);
    setDraft({ email: '', firstName: '', lastName: '', disciplineId: '', license: '', phone: '' });
    setNotice(t('professionals.invited', { email: draft.email.trim() }));
    load();
  };

  const toggleBlock = async (therapist) => {
    const blocking = !therapist.is_blocked;
    if (!window.confirm(t(blocking ? 'professionals.confirmBlock' : 'professionals.confirmUnblock',
      { name: `${therapist.first_name} ${therapist.last_name}` }))) return;
    await supabase.from('therapists')
      .update({
        is_blocked: blocking,
        blocked_at: blocking ? new Date().toISOString() : null,
      })
      .eq('id', therapist.id);
    load();
  };

  const saveEdit = async () => {
    setBusy(true);
    const { error: saveError } = await supabase
      .from('therapists')
      .update({ primary_discipline_id: editing.primary_discipline_id || null })
      .eq('id', editing.id);

    setBusy(false);
    if (saveError) { setError(saveError.message); return; }
    setEditing(null);
    setNotice(t('professionals.saved'));
    load();
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('nav.professionals')}</h1>
          <p className="text-sm text-slate-500">{t('professionals.subtitle')}</p>
        </div>
        <Button onClick={() => setInviting(true)}><Plus size={16} />{t('professionals.add')}</Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {pending.length > 0 && (
        <Card title={t('professionals.pendingRequests')} subtitle={t('professionals.pendingSubtitle')}>
          <ul className="space-y-3">
            {pending.map((request) => (
              <li key={request.id} className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {request.first_name} {request.last_name}
                    </p>
                    <p className="text-xs text-slate-600">{request.email}</p>
                    <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                      <div className="flex gap-2">
                        <dt className="text-slate-500">{t('specializations.discipline')}:</dt>
                        <dd className="font-medium">{request.disciplines?.name ?? t('professionals.noDiscipline')}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-slate-500">{t('professionals.license')}:</dt>
                        <dd className="font-medium">{request.license_number ?? '—'}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-slate-500">{t('professionals.requestedAt')}:</dt>
                        <dd className="font-medium">{request.approval_requested_at ? formatDateTime(request.approval_requested_at) : '—'}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-slate-500">{t('professionals.feeAccepted')}:</dt>
                        <dd className="font-medium">
                          {request.fee_accepted_at
                            ? formatMoney(request.fee_accepted_monthly ?? 0)
                            : t('common.no')}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-xs text-amber-800">{t('professionals.verifyLicenseHint')}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button variant="success" loading={busy} onClick={() => review(request, true)}>
                      {t('patients.approve')}
                    </Button>
                    <Button variant="secondary" loading={busy} onClick={() => review(request, false)}>
                      {t('patients.decline')}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        {roster.length === 0 ? (
          <EmptyState icon={UserCog} title={t('professionals.none')} />
        ) : (
          <Table headers={[
            t('appointments.professional'), t('specializations.discipline'),
            t('locations.title'), t('settings.sessionLength'), t('common.status'), '',
          ]}>
            {roster.map((therapist) => {
              const places = placesOf(assignments[therapist.id]);
              return (
                <tr key={therapist.id} className={therapist.is_blocked ? 'opacity-60' : undefined}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">
                        {therapist.first_name} {therapist.last_name}
                      </span>
                      {therapist.is_admin && <Badge tone="indigo">{t('roles.admin')}</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">{therapist.email}</p>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {therapist.disciplines?.name ?? (
                      <span className="flex items-center gap-1 text-amber-700">
                        <AlertTriangle size={12} />{t('professionals.noDiscipline')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {places.length === 0
                      ? <span className="text-slate-400">{t('professionals.noHours')}</span>
                      : places.join(', ')}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {therapist.default_session_minutes} {t('settings.minutes')}
                  </td>
                  <td className="px-3 py-3">
                    {therapist.is_blocked ? <Badge tone="danger">{t('patients.blocked')}</Badge>
                      : !therapist.auth_user_id ? (
                        <Badge tone="warning"><Mail size={11} />{t('professionals.pendingClaim')}</Badge>
                      ) : <Badge tone="success">{t('professionals.active')}</Badge>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        onClick={() => setEditing({ ...therapist, places })}
                        title={t('common.edit')}
                      >
                        <Stethoscope size={14} />
                      </Button>
                      {!therapist.is_admin && (
                        <Button variant="ghost" onClick={() => toggleBlock(therapist)}
                          title={therapist.is_blocked ? t('patients.unblock') : t('patients.block')}>
                          {therapist.is_blocked ? <RotateCcw size={14} /> : <Ban size={14} />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <Modal
        open={inviting}
        title={t('professionals.add')}
        onClose={() => setInviting(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviting(false)}>{t('common.cancel')}</Button>
            <Button onClick={invite} loading={busy}>{t('professionals.sendInvite')}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Alert tone="info">{t('professionals.inviteExplanation')}</Alert>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('register.firstName')} required>
              <Input value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
            </Field>
            <Field label={t('register.lastName')} required>
              <Input value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
            </Field>
          </div>
          <Field label={t('auth.email')} required hint={t('professionals.emailHint')}>
            <Input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('specializations.primaryDiscipline')} hint={t('professionals.disciplineHint')}>
              <Select value={draft.disciplineId} onChange={(e) => setDraft({ ...draft, disciplineId: e.target.value })}>
                <option value="">{t('common.none')}</option>
                {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label={t('professionals.license')}>
              <Input value={draft.license} onChange={(e) => setDraft({ ...draft, license: e.target.value })} />
            </Field>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(editing)}
        title={editing ? `${editing.first_name} ${editing.last_name}` : ''}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEdit} loading={busy}>{t('common.save')}</Button>
          </>
        }
      >
        {editing && (
          <div className="space-y-4">
            <Field label={t('specializations.primaryDiscipline')} hint={t('professionals.disciplineHint')}>
              <Select
                value={editing.primary_discipline_id ?? ''}
                onChange={(e) => setEditing({ ...editing, primary_discipline_id: e.target.value })}
              >
                <option value="">{t('common.none')}</option>
                {disciplines.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>

            {/* Read only, like the sedes below: each professional sets it with
                their hours in Configuración, so nobody else overwrites it. */}
            <Field label={t('settings.sessionLength')} hint={t('professionals.sessionLengthHint')}>
              <p className="text-sm text-slate-700">
                {editing.default_session_minutes} {t('settings.minutes')}
              </p>
            </Field>

            {/* Read only: a professional's sedes are where they put their hours
                in Configuración (0044), not a list ticked here. */}
            <Field label={t('locations.title')} hint={t('professionals.placesHint')}>
              <p className="flex items-center gap-1.5 text-sm text-slate-700">
                <MapPin size={13} className="text-slate-400" />
                {editing.places.length ? editing.places.join(', ') : t('professionals.noHours')}
              </p>
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProfessionalsView;
